import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { bulkPreviewInvoices, bulkCreateInvoices, bulkPostInvoices } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_INVOICES } from '../../../../Routing/routes';
import { addDaysToDateString, getDueDateError } from '../../../../utils/helpers/universalFunctions';

// Head types that the user can bill as invoice line items
const INVOICE_ITEM_HEAD_TYPES = ['CHARGE', 'DISCOUNT'];

const BulkCreateInvoice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const societyId = selectedSocietyStorer.getSocietyId();

    // Redux selectors
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown, flatsDropdownLoading } = useSelector(state => state.WingsAndFlatsReducers || {});
    const { bulkPreviewLoading, bulkCreateLoading, bulkPostLoading } = useSelector(state => state.InvoiceReducer || {});

    // Wizard step: 'setup' (pick flats) -> 'preview' (review/edit amounts)
    const [step, setStep] = useState('setup');
    // Preview result kept in local state (set from the saga onSuccess) so rendering
    // doesn't depend on reducer timing — same pattern as the post-create summary below.
    const [preview, setPreview] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        invoice_type_id: '',
        invoice_date: localDateString(),
        due_date: '',
        start_period: '',
        end_period: '',
        include_debit_note_arrear: false,
        include_credit_note: false
    });

    // Wing filter for flat selection
    const [selectedWing, setSelectedWing] = useState('all');
    // Selected flat IDs
    const [selectedFlatIds, setSelectedFlatIds] = useState([]);
    // Per-flat editable NONE-head amounts: { [flat_id]: { [head_id]: string } }
    const [noneAmounts, setNoneAmounts] = useState({});
    // Summary dialog (after drafts are created)
    const [showSummary, setShowSummary] = useState(false);
    const [summaryData, setSummaryData] = useState(null);

    // Format period name helper (e.g., "January 2026" or "January 2026 - March 2026")
    const formatPeriodName = (startDate, endDate, frequency) => {
        if (!startDate) return '';

        const start = new Date(startDate);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                           'July', 'August', 'September', 'October', 'November', 'December'];

        const startMonth = monthNames[start.getMonth()];
        const startYear = start.getFullYear();

        if (frequency === -1 || !endDate) {
            return `${startMonth} ${startYear}`;
        }

        const end = new Date(endDate);
        const endMonth = monthNames[end.getMonth()];
        const endYear = end.getFullYear();

        if (startMonth === endMonth && startYear === endYear) {
            return `${startMonth} ${startYear}`;
        }

        return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
    };

    // Format a Date as YYYY-MM-DD using LOCAL time (avoids UTC offset shifting the date)
    const toLocalDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Fetch dropdown data on mount
    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    // Fetch flats when wing filter changes
    useEffect(() => {
        if (selectedWing && selectedWing !== 'all') {
            dispatch(getFlatsDropdown({ wing_id: selectedWing }));
        } else if (selectedWing === 'all') {
            dispatch(getFlatsDropdown({ society_id: societyId }));
        }
    }, [dispatch, selectedWing, societyId]);

    // Auto-calculate end_period based on invoice type frequency
    useEffect(() => {
        if (!formData.start_period || !formData.invoice_type_id) return;

        const invoiceTypes = invoiceTypesData || [];
        const selectedType = invoiceTypes.find(t => t.invoice_type_id === parseInt(formData.invoice_type_id));
        if (!selectedType) return;

        // frequency comes from the API as a string (e.g. "1"); parse it so the
        // arithmetic below stays numeric (otherwise getMonth() + "1" concatenates).
        const frequency = parseInt(selectedType.frequency, 10) || 1;
        if (frequency === -1) {
            // One-time: end_period same as start
            setFormData(prev => ({ ...prev, end_period: prev.start_period }));
            return;
        }

        const start = new Date(formData.start_period);
        const end = new Date(start);
        end.setMonth(end.getMonth() + frequency);
        end.setDate(end.getDate() - 1);
        setFormData(prev => ({ ...prev, end_period: toLocalDate(end) }));
    }, [formData.start_period, formData.invoice_type_id, invoiceTypesData]);

    // Flats array from dropdown
    const flatsArray = useMemo(() => {
        if (Array.isArray(flatsDropdown)) return flatsDropdown;
        if (flatsDropdown?.data && Array.isArray(flatsDropdown.data)) return flatsDropdown.data;
        return [];
    }, [flatsDropdown]);

    // Wing titles come as "A Wing" / "B Wing" - keep just the identifier so flats read "B-102"
    const shortWing = (title) => String(title || '').replace(/\s*wing\s*$/i, '').trim() || String(title || '');

    // Wing of a flat, from its own payload or the wings dropdown
    const resolveWing = useCallback((flat) => {
        const own = flat?.wing?.title || flat?.wing_title || flat?.wing?.number;
        if (own) return shortWing(own);

        const fromDropdown = (wingsDropdown || []).find(w => w.wing_id === flat?.wing_id);
        return fromDropdown ? shortWing(fromDropdown.title || fromDropdown.number) : '';
    }, [wingsDropdown]);

    // Preview and summary rows come back from the invoice API without wing details, so
    // remember the wing of every flat we list - selections survive a change of wing filter.
    const [wingByFlatId, setWingByFlatId] = useState({});
    useEffect(() => {
        if (flatsArray.length === 0) return;
        setWingByFlatId(prev => {
            const next = { ...prev };
            flatsArray.forEach(flat => {
                const wing = resolveWing(flat);
                if (wing) next[flat.flat_id] = wing;
            });
            return next;
        });
    }, [flatsArray, resolveWing]);

    // "B-102", or just the flat number when the wing is unknown
    const flatLabel = (flat) => {
        const flatNumber = flat?.flat_number || flat?.flat_name || (flat?.flat_id ? `#${flat.flat_id}` : '');
        const wing = resolveWing(flat) || wingByFlatId[flat?.flat_id] || '';
        return wing && flatNumber ? `${wing}-${flatNumber}` : `${flatNumber}`;
    };

    // Compute period display name (e.g., "January 2026") from the selected type's frequency
    const periodDisplayName = useMemo(() => {
        if (!formData.start_period || !formData.invoice_type_id) return '';
        const selectedType = (invoiceTypesData || []).find(t => t.invoice_type_id === parseInt(formData.invoice_type_id));
        const frequency = parseInt(selectedType?.frequency, 10) || 1;
        return formatPeriodName(formData.start_period, formData.end_period, frequency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.start_period, formData.end_period, formData.invoice_type_id, invoiceTypesData]);

    // Preview data
    const previewFlats = useMemo(() => preview?.flats || [], [preview]);
    const eligibleFlats = useMemo(() => previewFlats.filter(f => f.eligible), [previewFlats]);
    const skippedFlats = useMemo(() => previewFlats.filter(f => !f.eligible), [previewFlats]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
            // Drop a due date that is no longer after the invoice date
            ...(name === 'invoice_date' && getDueDateError(value, prev.due_date) ? { due_date: '' } : {})
        }));
    };

    // Due date must be after the invoice date - shown inline and blocks both wizard steps
    const dueDateError = getDueDateError(formData.invoice_date, formData.due_date);
    const minDueDate = addDaysToDateString(formData.invoice_date, 1);

    const toggleFlatSelection = (flatId) => {
        setSelectedFlatIds(prev =>
            prev.includes(flatId)
                ? prev.filter(id => id !== flatId)
                : [...prev, flatId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedFlatIds.length === flatsArray.length) {
            setSelectedFlatIds([]);
        } else {
            setSelectedFlatIds(flatsArray.map(f => f.flat_id));
        }
    };

    const isFormValid = () => {
        return formData.invoice_type_id &&
            formData.invoice_date &&
            formData.due_date &&
            formData.start_period &&
            formData.end_period &&
            !dueDateError &&
            selectedFlatIds.length > 0;
    };

    // Charge/discount heads the user can edit (NONE type) vs auto-calculated (FIXED/METERED/etc.)
    const getEditableHeads = (flat) => (flat.tariff_heads || []).filter(
        h => INVOICE_ITEM_HEAD_TYPES.includes(h.headType?.type_name) && h.calculation_type === 'NONE'
    );
    const getAutoChargeHeads = (flat) => (flat.tariff_heads || []).filter(
        h => INVOICE_ITEM_HEAD_TYPES.includes(h.headType?.type_name) && h.calculation_type !== 'NONE'
    );

    // Amount shown for an auto-calculated head: the user's override when they
    // have typed one, otherwise the server's computed figure.
    const amountFor = (flatId, head) => {
        const typed = noneAmounts[flatId]?.[head.head_id];
        if (typed !== undefined && typed !== '') return typed;
        return head.calculated_amount != null ? String(head.calculated_amount) : '';
    };

    const isOverridden = (flatId, head) => {
        const typed = noneAmounts[flatId]?.[head.head_id];
        if (typed === undefined || typed === '') return false;
        return parseFloat(typed) !== parseFloat(head.calculated_amount ?? 0);
    };

    // Sum of the NONE amounts the user entered for a flat
    const flatNoneTotal = (flat) => getEditableHeads(flat).reduce(
        (sum, h) => sum + (parseFloat(noneAmounts[flat.flat_id]?.[h.head_id]) || 0), 0
    );

    // Auto-calculated charges for a flat, with any edited amounts substituted.
    // estimated_charges is the server's untouched sum, so it cannot be used once
    // a figure has been overridden on this screen.
    const flatAutoTotal = (flat) => getAutoChargeHeads(flat).reduce(
        (sum, h) => sum + (parseFloat(amountFor(flat.flat_id, h)) || 0), 0
    );

    // Estimated net per flat (authoritative amounts are computed server-side on draft)
    const flatEstimatedTotal = (flat) => {
        const a = flat.arrears || {};
        return flatAutoTotal(flat)
            + flatNoneTotal(flat)
            + (parseFloat(a.principal_arrear) || 0)
            + (parseFloat(a.debit_note_arrear) || 0)
            + (parseFloat(a.interest) || 0)
            - (parseFloat(a.adjustment) || 0);
    };

    const handleNoneAmountChange = (flatId, headId, value) => {
        setNoneAmounts(prev => ({
            ...prev,
            [flatId]: { ...(prev[flatId] || {}), [headId]: value }
        }));
    };

    // Step 1 -> request the preview
    const handlePreview = (e) => {
        e.preventDefault();
        if (!isFormValid()) return;

        if (dueDateError) {
            alert(dueDateError);
            return;
        }

        dispatch(bulkPreviewInvoices({
            society_id: societyId,
            invoice_type_id: parseInt(formData.invoice_type_id),
            flat_ids: selectedFlatIds,
            start_period: formData.start_period,
            end_period: formData.end_period,
            include_debit_note_arrear: formData.include_debit_note_arrear,
            include_credit_note: formData.include_credit_note,
            onSuccess: (data) => {
                setPreview(data);
                // Seed editable NONE amounts (default blank = 0) for eligible flats
                const init = {};
                (data?.flats || []).forEach(f => {
                    if (!f.eligible) return;
                    init[f.flat_id] = {};
                    (f.tariff_heads || []).forEach(h => {
                        if (INVOICE_ITEM_HEAD_TYPES.includes(h.headType?.type_name) && h.calculation_type === 'NONE') {
                            init[f.flat_id][h.head_id] = '';
                        }
                    });
                });
                setNoneAmounts(init);
                setStep('preview');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }));
    };

    // Step 2 -> draft the invoices with per-flat edited amounts
    const handleCreateDrafts = () => {
        if (eligibleFlats.length === 0) return;

        // Re-check here too - step 2 dispatches the dates that step 1 collected
        if (dueDateError) {
            alert(dueDateError);
            handleBackToSetup();
            return;
        }

        const flats = eligibleFlats.map(f => ({
            flat_id: f.flat_id,
            items: [
                // Manual (NONE) heads: only meaningful when an amount was typed.
                ...getEditableHeads(f)
                    .map(h => ({ head_id: h.head_id, amount: parseFloat(noneAmounts[f.flat_id]?.[h.head_id]) || 0 }))
                    .filter(item => item.amount > 0),
                // Auto-calculated heads: sent only where the figure was actually
                // edited, so untouched heads keep being computed server-side and
                // stay in step with the rate configuration.
                ...getAutoChargeHeads(f)
                    .filter(h => isOverridden(f.flat_id, h))
                    .map(h => ({ head_id: h.head_id, amount: parseFloat(noneAmounts[f.flat_id]?.[h.head_id]) || 0 }))
            ]
        }));

        dispatch(bulkCreateInvoices({
            society_id: societyId,
            invoice_type_id: parseInt(formData.invoice_type_id),
            flats,
            invoice_date: formData.invoice_date,
            due_date: formData.due_date,
            start_period: formData.start_period,
            end_period: formData.end_period,
            include_debit_note_arrear: formData.include_debit_note_arrear,
            include_credit_note: formData.include_credit_note,
            onSuccess: (data) => {
                setSummaryData(data);
                setShowSummary(true);
            }
        }));
    };

    const handleBackToSetup = () => {
        setStep('setup');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePostCreated = () => {
        if (!summaryData?.created?.length) return;
        const invoiceIds = summaryData.created.map(inv => inv.invoice_id);
        dispatch(bulkPostInvoices({
            invoice_ids: invoiceIds,
            onSuccess: () => {
                setShowSummary(false);
                navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES);
            }
        }));
    };

    const handleCloseSummary = () => {
        setShowSummary(false);
        navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES);
    };

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const typeBadgeClass = (calcType) => {
        switch (calcType) {
            case 'FIXED': return 'tw-bg-sky-100 tw-text-sky-700 tw-border tw-border-sky-200';
            case 'METERED': return 'tw-bg-emerald-100 tw-text-emerald-700 tw-border tw-border-emerald-200';
            case 'FORMULA': return 'tw-bg-purple-100 tw-text-purple-700 tw-border tw-border-purple-200';
            case 'AREA_BASED': return 'tw-bg-amber-100 tw-text-amber-700 tw-border tw-border-amber-200';
            default: return 'tw-bg-gray-100 tw-text-gray-700 tw-border tw-border-gray-200';
        }
    };

    return (
        <div className="tw-p-6 tw-max-w-5xl tw-mx-auto">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-6">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <button
                        onClick={() => step === 'preview' ? handleBackToSetup() : navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES)}
                        className="tw-p-1.5 tw-rounded-md tw-text-gray-500 hover:tw-bg-gray-100"
                    >
                        <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                    </button>
                    <div>
                        <h1 className="tw-text-lg tw-font-semibold tw-text-gray-800">Bulk Create Invoices</h1>
                        <p className="tw-text-xs tw-text-gray-500">
                            {step === 'setup'
                                ? 'Step 1 of 2 — Select flats and billing settings'
                                : 'Step 2 of 2 — Review & edit amounts, then create drafts'}
                        </p>
                    </div>
                </div>
            </div>

            {/* ============ STEP 1: SETUP ============ */}
            {step === 'setup' && (
            <form onSubmit={handlePreview}>
                {/* Invoice Settings Card */}
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-5 tw-mb-5">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4">Invoice Settings</h2>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        {/* Invoice Type */}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                Invoice Type <span className="tw-text-red-500">*</span>
                            </label>
                            <select
                                name="invoice_type_id"
                                value={formData.invoice_type_id}
                                onChange={handleChange}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                required
                            >
                                <option value="">Select Invoice Type</option>
                                {(invoiceTypesData || []).map(type => (
                                    <option key={type.invoice_type_id} value={type.invoice_type_id}>
                                        {type.invoice_title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Invoice Date */}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                Invoice Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="invoice_date"
                                value={formData.invoice_date}
                                onChange={handleChange}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                required
                            />
                        </div>

                        {/* Due Date */}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                Due Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="due_date"
                                value={formData.due_date}
                                min={minDueDate || undefined}
                                onChange={handleChange}
                                className={`tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${dueDateError ? 'tw-border-red-400 focus:tw-ring-red-500' : 'tw-border-gray-300 focus:tw-ring-blue-500'}`}
                                required
                            />
                            {dueDateError && (
                                <p className="tw-mt-1 tw-text-xs tw-text-red-600">{dueDateError}</p>
                            )}
                        </div>

                        {/* Start Period */}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                Billing Period Start <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="start_period"
                                value={formData.start_period}
                                onChange={handleChange}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                required
                            />
                        </div>

                        {/* End Period (auto-calculated from invoice type frequency) */}
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">
                                Billing Period End <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="end_period"
                                value={formData.end_period}
                                onChange={handleChange}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-2 tw-text-sm tw-bg-gray-50 tw-text-gray-500 tw-cursor-not-allowed focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                required
                                readOnly
                            />
                            {periodDisplayName && (
                                <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1.5">
                                    <Icon icon="mdi:calendar-range" className="tw-text-blue-500 tw-text-sm" />
                                    <span className="tw-text-xs tw-font-semibold tw-text-blue-700 tw-bg-blue-50 tw-px-2 tw-py-1 tw-rounded-md tw-border tw-border-blue-100">
                                        {periodDisplayName}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="tw-flex tw-gap-6 tw-mt-4">
                        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700 tw-cursor-pointer">
                            <input
                                type="checkbox"
                                name="include_debit_note_arrear"
                                checked={formData.include_debit_note_arrear}
                                onChange={handleChange}
                                className="form-check-input"
                            />
                            Include Debit Note Arrear
                        </label>
                        <label className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700 tw-cursor-pointer">
                            <input
                                type="checkbox"
                                name="include_credit_note"
                                checked={formData.include_credit_note}
                                onChange={handleChange}
                                className="form-check-input"
                            />
                            Include Credit Note
                        </label>
                    </div>
                </div>

                {/* Flat Selection Card */}
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-5 tw-mb-5">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-700">
                            Select Flats <span className="tw-text-red-500">*</span>
                            {selectedFlatIds.length > 0 && (
                                <span className="tw-ml-2 tw-text-xs tw-font-normal tw-text-blue-600">
                                    ({selectedFlatIds.length} selected)
                                </span>
                            )}
                        </h2>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            {/* Wing Filter */}
                            <select
                                value={selectedWing}
                                onChange={(e) => {
                                    setSelectedWing(e.target.value);
                                    setSelectedFlatIds([]);
                                }}
                                className="tw-border tw-border-gray-300 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                            >
                                <option value="all">All Wings</option>
                                {(wingsDropdown || []).map(wing => (
                                    <option key={wing.wing_id} value={wing.wing_id}>
                                        {wing.title}
                                    </option>
                                ))}
                            </select>
                            {/* Select All */}
                            <button
                                type="button"
                                onClick={toggleSelectAll}
                                className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-700 tw-font-medium"
                            >
                                {selectedFlatIds.length === flatsArray.length && flatsArray.length > 0 ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    </div>

                    {/* Flats Grid */}
                    {flatsDropdownLoading ? (
                        <div className="tw-flex tw-items-center tw-justify-center tw-py-8 tw-text-gray-400">
                            <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-mr-2" />
                            Loading flats...
                        </div>
                    ) : flatsArray.length === 0 ? (
                        <div className="tw-text-center tw-py-8 tw-text-gray-400 tw-text-sm">
                            No flats found. Select a wing or check your setup.
                        </div>
                    ) : (
                        <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-4 lg:tw-grid-cols-6 tw-gap-2 tw-max-h-72 tw-overflow-y-auto tw-pr-1">
                            {flatsArray.map(flat => {
                                const isSelected = selectedFlatIds.includes(flat.flat_id);
                                return (
                                    <button
                                        key={flat.flat_id}
                                        type="button"
                                        onClick={() => toggleFlatSelection(flat.flat_id)}
                                        className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-md tw-border tw-text-sm tw-transition-colors ${
                                            isSelected
                                                ? 'tw-bg-blue-50 tw-border-blue-400 tw-text-blue-700'
                                                : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-border-gray-300'
                                        }`}
                                    >
                                        {flatLabel(flat)}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Submit */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_INVOICES)}
                        className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!isFormValid() || bulkPreviewLoading}
                        className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {bulkPreviewLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                        {bulkPreviewLoading ? 'Generating Preview...' : `Preview ${selectedFlatIds.length} Invoice(s)`}
                        {!bulkPreviewLoading && <Icon icon="mdi:arrow-right" />}
                    </button>
                </div>
            </form>
            )}

            {/* ============ STEP 2: PREVIEW & EDIT ============ */}
            {step === 'preview' && (
            <div>
                {/* Summary bar */}
                <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-5 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-4">
                        <div className="tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:calendar-range" className="tw-text-blue-500" />
                            <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{preview?.period || periodDisplayName}</span>
                        </div>
                        <span className="tw-text-xs tw-text-emerald-700 tw-bg-emerald-50 tw-px-2 tw-py-1 tw-rounded-md tw-border tw-border-emerald-100">
                            {eligibleFlats.length} ready
                        </span>
                        {skippedFlats.length > 0 && (
                            <span className="tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-px-2 tw-py-1 tw-rounded-md tw-border tw-border-amber-100">
                                {skippedFlats.length} skipped
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleBackToSetup}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-font-medium"
                    >
                        <Icon icon="mdi:pencil" /> Edit selection
                    </button>
                </div>

                {/* Hint */}
                <div className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-gray-500 tw-mb-4 tw-px-1">
                    <Icon icon="mdi:information-outline" className="tw-text-blue-400 tw-text-sm tw-mt-0.5" />
                    <span>Editable fields are <strong>manual (NONE)</strong> charge heads. All other heads, taxes, arrears and round-off are calculated automatically when the draft is created. Totals shown are estimates.</span>
                </div>

                {/* Eligible flats */}
                {eligibleFlats.length === 0 ? (
                    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-8 tw-text-center tw-text-gray-400 tw-text-sm tw-mb-5">
                        No flats are eligible for invoicing with the current selection.
                    </div>
                ) : (
                    <div className="tw-space-y-4 tw-mb-5">
                        {eligibleFlats.map(flat => {
                            const editableHeads = getEditableHeads(flat);
                            const autoHeads = getAutoChargeHeads(flat);
                            const a = flat.arrears || {};
                            return (
                                <div key={flat.flat_id} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                                    {/* Flat header */}
                                    <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                        <div className="tw-flex tw-items-center tw-gap-2.5">
                                            <Icon icon="mdi:home-city-outline" className="tw-text-blue-500 tw-text-lg" />
                                            <div>
                                                <div className="tw-text-sm tw-font-semibold tw-text-gray-800">Flat {flatLabel(flat)}</div>
                                                <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
                                                    <Icon icon="mdi:account-outline" className="tw-text-gray-400" />
                                                    <span>{flat.member_name || 'No owner on record'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="tw-text-right">
                                            <div className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wider">Est. Total</div>
                                            <div className="tw-text-sm tw-font-bold tw-text-blue-600">{formatCurrency(flatEstimatedTotal(flat))}</div>
                                        </div>
                                    </div>

                                    {/* Charge heads */}
                                    <div className="tw-px-5 tw-py-3">
                                        {(autoHeads.length === 0 && editableHeads.length === 0) ? (
                                            <p className="tw-text-xs tw-text-gray-400 tw-py-2">No charge heads configured for this invoice type.</p>
                                        ) : (
                                            <table className="tw-w-full tw-text-sm">
                                                <tbody className="tw-divide-y tw-divide-gray-100">
                                                    {/* Auto-calculated heads — amount is editable; an edited
                                                        figure overrides the rate for this flat only */}
                                                    {autoHeads.map(h => (
                                                        <tr key={h.head_id}>
                                                            <td className="tw-py-2">
                                                                <span className="tw-text-gray-800">{h.head_name}</span>
                                                                <span className={`tw-ml-2 tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold ${typeBadgeClass(h.calculation_type)}`}>
                                                                    {h.calculation_type}
                                                                </span>
                                                                {h.calculation_note && (
                                                                    <span className="tw-ml-2 tw-text-[11px] tw-text-amber-600">
                                                                        <Icon icon="mdi:alert-circle-outline" className="tw-inline tw-mr-0.5" />{h.calculation_note}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="tw-py-2 tw-text-right tw-w-40">
                                                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                                                    <span className="tw-text-xs tw-text-gray-400">Rs</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={amountFor(flat.flat_id, h)}
                                                                        onChange={(e) => handleNoneAmountChange(flat.flat_id, h.head_id, e.target.value)}
                                                                        title="Computed from the head's rate — edit to bill a different amount for this flat"
                                                                        className={`tw-w-24 tw-border tw-rounded-md tw-px-2 tw-py-1 tw-text-sm tw-text-right focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 ${
                                                                            isOverridden(flat.flat_id, h)
                                                                                ? 'tw-border-amber-400 tw-bg-amber-50 tw-font-semibold'
                                                                                : 'tw-border-gray-300'
                                                                        }`}
                                                                    />
                                                                </div>
                                                                {isOverridden(flat.flat_id, h) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleNoneAmountChange(flat.flat_id, h.head_id, '')}
                                                                        className="tw-text-[10px] tw-text-blue-600 hover:tw-underline tw-mt-0.5"
                                                                    >
                                                                        reset to {formatCurrency(h.calculated_amount)}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {/* Editable NONE heads */}
                                                    {editableHeads.map(h => (
                                                        <tr key={h.head_id} className="tw-bg-blue-50/30">
                                                            <td className="tw-py-2">
                                                                <span className="tw-text-gray-800">{h.head_name}</span>
                                                                <span className="tw-ml-2 tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-semibold tw-bg-gray-200 tw-text-gray-700">
                                                                    MANUAL
                                                                </span>
                                                            </td>
                                                            <td className="tw-py-2 tw-text-right tw-w-40">
                                                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                                                    <span className="tw-text-xs tw-text-gray-400">Rs</span>
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="0.01"
                                                                        value={noneAmounts[flat.flat_id]?.[h.head_id] ?? ''}
                                                                        onChange={(e) => handleNoneAmountChange(flat.flat_id, h.head_id, e.target.value)}
                                                                        placeholder="0.00"
                                                                        className="tw-w-24 tw-border tw-border-gray-300 tw-rounded-md tw-px-2 tw-py-1 tw-text-sm tw-text-right focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                                                                    />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}

                                        {/* Arrears / interest summary */}
                                        {(a.principal_arrear > 0 || a.debit_note_arrear > 0 || a.interest > 0 || a.adjustment > 0) && (
                                            <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-dashed tw-border-gray-200 tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1 tw-text-xs tw-text-gray-500">
                                                {a.principal_arrear > 0 && <span>Arrears: <strong className="tw-text-gray-700">{formatCurrency(a.principal_arrear)}</strong></span>}
                                                {a.debit_note_arrear > 0 && <span>Debit Note: <strong className="tw-text-gray-700">{formatCurrency(a.debit_note_arrear)}</strong></span>}
                                                {a.interest > 0 && <span>Interest: <strong className="tw-text-gray-700">{formatCurrency(a.interest)}</strong></span>}
                                                {a.adjustment > 0 && <span>Adjustment: <strong className="tw-text-emerald-600">-{formatCurrency(a.adjustment)}</strong></span>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Skipped flats */}
                {skippedFlats.length > 0 && (
                    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-5 tw-mb-5">
                        <h3 className="tw-text-xs tw-font-semibold tw-text-amber-700 tw-uppercase tw-tracking-wide tw-mb-3">
                            Skipped ({skippedFlats.length})
                        </h3>
                        <div className="tw-space-y-1.5">
                            {skippedFlats.map((f, idx) => (
                                <div key={idx} className="tw-flex tw-items-center tw-justify-between tw-bg-amber-50 tw-px-3 tw-py-2 tw-rounded-md tw-text-sm">
                                    <div className="tw-flex tw-items-center tw-gap-2">
                                        <Icon icon="mdi:alert-circle" className="tw-text-amber-500" />
                                        <span className="tw-text-gray-700">Flat {flatLabel(f)}</span>
                                    </div>
                                    <span className="tw-text-xs tw-text-gray-500">{f.reason}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                    <button
                        type="button"
                        onClick={handleBackToSetup}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:arrow-left" /> Back
                    </button>
                    <button
                        type="button"
                        onClick={handleCreateDrafts}
                        disabled={eligibleFlats.length === 0 || bulkCreateLoading}
                        className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-emerald-600 tw-rounded-md hover:tw-bg-emerald-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        {bulkCreateLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                        {bulkCreateLoading ? 'Creating Drafts...' : `Create ${eligibleFlats.length} Draft(s)`}
                    </button>
                </div>
            </div>
            )}

            {/* Summary Dialog */}
            {showSummary && summaryData && (
                <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-lg tw-mx-4 tw-max-h-[80vh] tw-flex tw-flex-col">
                        {/* Summary Header */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-200">
                            <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">Bulk Create Summary</h3>
                            <button onClick={handleCloseSummary} className="tw-text-gray-400 hover:tw-text-gray-600">
                                <Icon icon="mdi:close" className="tw-text-xl" />
                            </button>
                        </div>

                        {/* Summary Body */}
                        <div className="tw-px-5 tw-py-4 tw-overflow-y-auto tw-flex-1">
                            {/* Stats Row */}
                            <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mb-4">
                                <div className="tw-bg-gray-50 tw-rounded-md tw-p-3 tw-text-center">
                                    <div className="tw-text-lg tw-font-bold tw-text-gray-700">{summaryData.total_requested || 0}</div>
                                    <div className="tw-text-xs tw-text-gray-500">Requested</div>
                                </div>
                                <div className="tw-bg-emerald-50 tw-rounded-md tw-p-3 tw-text-center">
                                    <div className="tw-text-lg tw-font-bold tw-text-emerald-700">{summaryData.total_created || 0}</div>
                                    <div className="tw-text-xs tw-text-emerald-600">Created</div>
                                </div>
                                <div className="tw-bg-amber-50 tw-rounded-md tw-p-3 tw-text-center">
                                    <div className="tw-text-lg tw-font-bold tw-text-amber-700">{summaryData.total_skipped || 0}</div>
                                    <div className="tw-text-xs tw-text-amber-600">Skipped</div>
                                </div>
                            </div>

                            {/* Created Invoices */}
                            {summaryData.created?.length > 0 && (
                                <div className="tw-mb-4">
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-2">
                                        Created Successfully
                                    </h4>
                                    <div className="tw-space-y-1.5">
                                        {summaryData.created.map(inv => (
                                            <div key={inv.invoice_id} className="tw-flex tw-items-center tw-justify-between tw-bg-emerald-50 tw-px-3 tw-py-2 tw-rounded-md tw-text-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <Icon icon="mdi:check-circle" className="tw-text-emerald-500" />
                                                    <span className="tw-text-gray-700">{flatLabel(inv)}</span>
                                                </div>
                                                <span className="tw-font-medium tw-text-gray-800">{formatCurrency(inv.net_payable)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Skipped Invoices */}
                            {summaryData.skipped?.length > 0 && (
                                <div>
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-amber-700 tw-uppercase tw-tracking-wide tw-mb-2">
                                        Skipped
                                    </h4>
                                    <div className="tw-space-y-1.5">
                                        {summaryData.skipped.map((item, idx) => (
                                            <div key={idx} className="tw-flex tw-items-center tw-justify-between tw-bg-amber-50 tw-px-3 tw-py-2 tw-rounded-md tw-text-sm">
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <Icon icon="mdi:alert-circle" className="tw-text-amber-500" />
                                                    <span className="tw-text-gray-700">{flatLabel(item)}</span>
                                                </div>
                                                <span className="tw-text-xs tw-text-gray-500">{item.reason}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Summary Footer */}
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-5 tw-py-4 tw-border-t tw-border-gray-200">
                            <button
                                onClick={handleCloseSummary}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
                            >
                                Close
                            </button>
                            {summaryData.created?.length > 0 && (
                                <button
                                    onClick={handlePostCreated}
                                    disabled={bulkPostLoading}
                                    className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-emerald-600 tw-rounded-md hover:tw-bg-emerald-700 disabled:tw-opacity-50"
                                >
                                    {bulkPostLoading && <Icon icon="mdi:loading" className="tw-animate-spin" />}
                                    {bulkPostLoading ? 'Posting...' : `Post ${summaryData.created.length} Invoice(s)`}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BulkCreateInvoice;
